import { withTheme } from '@digital-retail/falabella-ui-cross-components';
import { isSodimacStandalone } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { getTenantValue } from '../../../containers/ProdctSpecifications/BuyTogether/constants';
import { isWindow } from '../../../utils/deviceChecker';
import { withLabels } from '../../../utils/LabelsContext';
import _ from '../../../utils/LodashImports';
import { noop } from '../../../utils/noop';
import { carouselActions, recoAnalytics } from '../../../utils/recoAnalytics';
import useIntersection from '../../../utils/useIntersection';
import useLazyLoadImage from '../../../utils/useLazyLoadImage';
import { Badges } from '../../Badges/Badges';
import Prices from '../../Prices/Prices';
import Image from '../../ui/Image/Image';
import RatingsVisual from '../../ui/RatingsVisual/RatingsVisual';
import Reviews from '../../ui/Reviews/Reviews';
import { ComparativeTablePodStyles, DriverBadgeStyles } from './ComparativeTablePod.style';

const RatingStars = dynamic(() => import('@digital-retail/rating-and-reviews').then((lib) => lib.RatingStars), {
  ssr: false,
  loading: () => '',
});

const DriverBadge = ({ driverBadge }) => {
  const badgeStyles = DriverBadgeStyles(driverBadge);
  return (
    <div className="driver-badge">
      <span className="driver-badge-label">{driverBadge?.label}</span>
      <style jsx>{badgeStyles}</style>
    </div>
  );
};

DriverBadge.defaultProps = {
  driverBadge: {},
};

DriverBadge.propTypes = {
  driverBadge: PropTypes.object,
};

const ComparativeTablePod = ({
  product,
  appCtx,
  labels,
  details,
  aggregateViewedProducts,
  addToCart,
  podClicked,
  addedToCart,
  totalProducts,
  currentVariant,
  slot,
  theme,
  showDriverInComparativeTable,
}) => {
  const themedStyles = ComparativeTablePodStyles(theme);

  const { deviceType } = appCtx;
  const productBrand = _.get(product, 'brand', '');
  const [loadImage, setLoadImage] = useState(false);
  const meatStickers = _.get(product, 'meatStickers', []);
  const isMoreThanOneRating = product.totalReviews && product.totalReviews > 0;
  const driverFirstPosition = details?.productPosition === 1 && showDriverInComparativeTable;
  const href = product.url && !driverFirstPosition ? product.url : '#';
  const badges = _.get(product, 'badges', []);
  const productRef = React.useRef(null);
  const isCarouselInViewport = useIntersection(productRef, '0px', '1', false);

  useLazyLoadImage({ productRef, setLoadImage });

  const trackSodimacClick = (action) => {
    recoAnalytics.click({
      carouselKey: 'CR_RecommendationCarousel',
      siteName: getTenantValue(appCtx?.store, appCtx?.regionCode),
      action,
      carouselName: details.carouselName,
      pageName: details.parentVariantID,
      skuId: product.id,
      podPositionInCarousel: details.productPosition,
      lengthPodsInCarousel: details.numberProducts,
      slotId: details.slotId,
    });
  };

  const trackSodimacImpression = (variantId) => {
    recoAnalytics.impression({
      carouselName: `CMP_${details.carouselName}`,
      skusViewed: [variantId],
      driverVariantId: details.parentVariantID,
    });
  };

  if (isWindow() && aggregateViewedProducts && isCarouselInViewport) {
    if (isSodimacStandalone(appCtx.store)) {
      trackSodimacImpression(product.id);
    } else {
      aggregateViewedProducts({
        sku: product.id,
        selectedTab: details.selectedTab,
      });
    }
  }

  const redirectToProduct = (e) => {
    if (driverFirstPosition) {
      e.preventDefault();
      return;
    }
    if (isSodimacStandalone(appCtx.store)) {
      trackSodimacClick(carouselActions.clickToRate);
      return;
    }
    const eventData = {
      carouselName: details.carouselName,
      currentProductID: details.parentVariantID,
      targetProductID: product.offeringId,
      numberProducts: details.numberProducts,
      productPosition: details.productPosition,
      carouselPosition: details.carouselPosition,
      country: getTenantValue(appCtx?.store, appCtx?.regionCode),
      store: appCtx.store,
    };
    podClicked(product.id);
    const eventToDigitalData = new CustomEvent('DDPDPCarouselProductClick', {
      bubbles: true,
      detail: eventData,
    });
    window.dispatchEvent(eventToDigitalData);
  };
  const imageSrc = product.mediaUrls && product.mediaUrls[0];
  const driverBadge = badges?.find((badge) => badge?.type === 'DRIVER_BADGE');

  const handleAddToCartClick = () => {
    addedToCart(product.id);
    if (isSodimacStandalone(appCtx.store)) {
      trackSodimacClick(carouselActions.addToCart);
    } else {
      const detail = {
        productToAdd: product.productId,
        tenant: getTenantValue(appCtx?.store, appCtx?.regionCode),
        currentVariant,
        totalProductInReco: totalProducts,
        position: details.productPosition,
        store: appCtx.store,
        slot,
        heading: details.carouselName,
      };
      const eventToDigitalData = new CustomEvent('DDPDPComparativeATC', {
        bubbles: true,
        detail,
      });
      window.dispatchEvent(eventToDigitalData);
    }
    addToCart({
      skuId: product.id,
      quantity: 1,
      offeringId: product.offeringId,
    });
  };

  return (
    <div className="comparative-table-pod-wrapper">
      {meatStickers && <Badges badgeItems={meatStickers} layout="PDP POD" isSticker />}
      <a
        href={href}
        id="testId-comparative-table-redirect"
        onClick={redirectToProduct}
        className={cx('pod-anchor', driverFirstPosition && 'first-product')}
        aria-hidden="true"
      >
        <div className="comparative-table-pod" ref={productRef}>
          <div className="comparative-table-pod-layout">
            <div className={cx('image-container', driverFirstPosition && 'first-product')}>
              <div className="image-wrapper">
                {driverBadge && (
                  <div className="driver-badge-wrapper">
                    <DriverBadge driverBadge={driverBadge} />
                  </div>
                )}
                {loadImage && (
                  <Image
                    id={product.id}
                    width={138}
                    height={115}
                    alt={product.displayName}
                    useSrcSet={false}
                    inlineDimensions
                    imageUrl={imageSrc}
                    type="comparative-pod"
                    lazyLoad
                  />
                )}
              </div>
            </div>
            <div className="pod-details">
              <div className="pod-product-brand">{productBrand}</div>
              <div className="pod-product-description">{product.displayName}</div>
              {driverFirstPosition && <RatingStars isComparativeTable={driverFirstPosition} />}
              {isMoreThanOneRating && (
                <div className="pod-rating">
                  <RatingsVisual value={Number(product.rating)} productId={product.productId} size="normal" />
                  <Reviews value={Number(product.totalReviews)} />
                </div>
              )}
              <div className="pod-product-price">
                <Prices
                  priceList={product.prices}
                  productId={product.productId}
                  discountBadge={product.discountBadge}
                  page="PDP"
                  layout="4_GRID"
                  variant="DESKTOP_4_GRID"
                  showMyList={false}
                />
              </div>
            </div>
          </div>
        </div>
      </a>
      <div className="pod-button">
        <btn
          id="testId-comparative-atc"
          className={`button ${isSodimacStandalone(appCtx.store) ? 'so_com' : ''}`}
          onClick={handleAddToCartClick}
        >
          <span className="add-to-cart-text">
            {deviceType === 'desktop' ? (
              labels.ADD_TO_CART_TEXT_MKP
            ) : (
              <div className="mobile-text">
                <span>{labels.ADD_TO_CART_TEXT_ALONE}</span>
                <div className="cart-icon" />
              </div>
            )}
          </span>
        </btn>
      </div>
      <style jsx>{themedStyles}</style>
    </div>
  );
};

ComparativeTablePod.propTypes = {
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  labels: PropTypes.object,
  details: PropTypes.object.isRequired,
  aggregateViewedProducts: PropTypes.func.isRequired,
  podClicked: PropTypes.func.isRequired,
  addedToCart: PropTypes.func.isRequired,
  addToCart: PropTypes.func,
  totalProducts: PropTypes.number.isRequired,
  currentVariant: PropTypes.string.isRequired,
  slot: PropTypes.string.isRequired,
  theme: PropTypes.object,
  showDriverInComparativeTable: PropTypes.bool,
};
ComparativeTablePod.defaultProps = {
  labels: { ADD_TO_CART: 'Ver producto' },
  addToCart: noop,
  theme: {},
  showDriverInComparativeTable: false,
};
export { ComparativeTablePod };
export default withTheme(withLabels(ComparativeTablePod));
